import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Button,
  Label,
  FormGroup,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormText,
} from 'reactstrap';

/**
 * Modal to create or edit a blog post with rich text editor
 */
function BlogModal({
  title = 'Post del Blog',
  isOpen = false,
  onToggle = () => {},
  defaultValue = null,
  onSubmit = () => {},
}) {
  const [titulo, setTitulo] = useState('');
  const [contenido, setContenido] = useState('');
  const [autor, setAutor] = useState('');
  const [categoria, setCategoria] = useState('');
  const [imagenDestacada, setImagenDestacada] = useState(null);
  const [preview, setPreview] = useState(null);

  // Quill editor modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: [] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
      ],
      ['link', 'image'],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['clean'],
    ],
  };

  const formats = [
    'header',
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'image',
    'color',
    'background',
    'align',
  ];

  // Update fields when defaultValue changes (for edit mode)
  useEffect(() => {
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setContenido(defaultValue.contenido || '');
      setAutor(defaultValue.autor || '');
      setCategoria(defaultValue.categoria || '');
      setPreview(defaultValue.imagen_destacada_url || null);
    } else {
      // Reset form for create mode
      setTitulo('');
      setContenido('');
      setAutor('');
      setCategoria('');
      setImagenDestacada(null);
      setPreview(null);
    }
  }, [defaultValue, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImagenDestacada(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (!titulo.trim() || !contenido.trim()) {
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append('titulo', titulo.trim());
    formData.append('contenido', contenido.trim());

    if (autor.trim()) {
      formData.append('autor', autor.trim());
    }
    if (categoria.trim()) {
      formData.append('categoria', categoria.trim());
    }

    // Only append image if it's a new file (for create) or if user uploaded a new one (for edit)
    if (imagenDestacada) {
      formData.append('imagen_destacada', imagenDestacada);
    }

    onSubmit(formData);
  };

  const handleCancel = () => {
    // Reset to original values
    if (defaultValue) {
      setTitulo(defaultValue.titulo || '');
      setContenido(defaultValue.contenido || '');
      setAutor(defaultValue.autor || '');
      setCategoria(defaultValue.categoria || '');
      setPreview(defaultValue.imagen_destacada_url || null);
    } else {
      setTitulo('');
      setContenido('');
      setAutor('');
      setCategoria('');
      setImagenDestacada(null);
      setPreview(null);
    }
    onToggle(); // Close modal
  };

  return (
    <Modal isOpen={isOpen} fade={false} toggle={onToggle} size="xl">
      <ModalHeader toggle={onToggle}>{title}</ModalHeader>
      <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        <FormGroup>
          <Label htmlFor="titulo">Título *</Label>
          <Input
            id="titulo"
            type="text"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ej: Título del post"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="contenido">Contenido *</Label>
          <ReactQuill
            theme="snow"
            value={contenido}
            onChange={setContenido}
            modules={modules}
            formats={formats}
            style={{ minHeight: '300px', marginBottom: '50px' }}
          />
          <FormText color="muted">
            Usa el editor de texto rico para formatear tu contenido
          </FormText>
        </FormGroup>

        <FormGroup>
          <Label htmlFor="imagen_destacada">
            Imagen Destacada (máx. 1200x800px)
          </Label>
          <Input
            id="imagen_destacada"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
          />
          <FormText color="muted">
            La imagen será redimensionada automáticamente si es mayor a
            1200x800px
          </FormText>
          {preview && (
            <div className="mt-2">
              <img
                src={preview}
                alt="Preview"
                style={{ maxWidth: '100%', maxHeight: '300px' }}
              />
            </div>
          )}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="autor">Autor</Label>
          <Input
            id="autor"
            type="text"
            value={autor}
            onChange={(e) => setAutor(e.target.value)}
            placeholder="Ej: Juan Pérez"
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="categoria">Categoría</Label>
          <Input
            id="categoria"
            type="text"
            value={categoria}
            onChange={(e) => setCategoria(e.target.value)}
            placeholder="Ej: Noticias, Tutoriales, etc."
          />
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="success" onClick={handleSubmit}>
          Guardar
        </Button>
        <Button color="secondary" onClick={handleCancel}>
          Cancelar
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default BlogModal;
